import React from 'react';
import { Minus, Plus } from 'lucide-react';

interface HoleInputProps {
  label: string;
  value: number | null;
  onChange: (value: number | null) => void;
  error?: string;
  placeholder?: string;
  disabled?: boolean;
  min?: number;
  max?: number;
  par?: number; // Default par value for the hole
}

export function HoleInput({
  label,
  value,
  onChange,
  error,
  placeholder = "Score",
  disabled = false,
  min = 1,
  max = 15,
  par = 4 // Default to par 4
}: HoleInputProps) {

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    if (newValue === '') {
      onChange(null);
    } else {
      const numValue = parseInt(newValue, 10);
      if (!isNaN(numValue) && numValue >= min && numValue <= max) {
        onChange(numValue);
      }
    }
  };

  const handleIncrement = () => {
    if (!disabled) {
      const currentValue = value || par;
      const newValue = currentValue + 1;
      if (newValue <= max) {
        onChange(newValue);
      }
    }
  };

  const handleDecrement = () => {
    if (!disabled) {
      const currentValue = value || par;
      const newValue = currentValue - 1;
      if (newValue >= min) {
        onChange(newValue);
      }
    }
  };

  const handleParClick = () => {
    if (!disabled) {
      onChange(par);
    }
  };

  // Use par as default display value if no score entered
  const displayValue = value !== null ? value : par;
  const isParScore = value === par;
  const isUnderPar = value !== null && value < par;
  const isOverPar = value !== null && value > par;

  return (
    <div className="space-y-3">
      {/* Label */}
      <label className="block text-sm font-medium text-gray-700 text-center">
        {label}
      </label>

      {/* Score Input Section - Mobile Optimized */}
      <div className="flex items-center justify-center gap-2">
        <button
          type="button"
          onClick={handleDecrement}
          disabled={disabled || displayValue <= min}
          className="flex items-center justify-center w-10 h-10 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation active:scale-95 transition-all"
        >
          <Minus className="w-5 h-5" />
        </button>

        <div className="flex flex-col items-center gap-1">
          <input
            type="number"
            value={displayValue}
            onChange={handleInputChange}
            placeholder={placeholder}
            disabled={disabled}
            min={min}
            max={max}
            className={`w-16 h-12 text-center text-2xl font-bold border-2 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 touch-manipulation transition-all ${
              error
                ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                : value === null
                ? 'border-gray-300 bg-gray-50 text-gray-500' // Default par value styling
                : isUnderPar
                ? 'border-green-400 bg-green-50 text-green-700'
                : isOverPar
                ? 'border-red-400 bg-red-50 text-red-700'
                : 'border-blue-400 bg-blue-50 text-blue-700' // Par score
            } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
          />
          
          {/* Par indicator */}
          <button
            type="button"
            onClick={handleParClick}
            disabled={disabled}
            className={`text-xs px-2 py-1 rounded-full transition-all touch-manipulation ${
              isParScore || value === null
                ? 'bg-blue-100 text-blue-700 font-medium'
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}
          >
            Par {par}
          </button>
        </div>

        <button
          type="button"
          onClick={handleIncrement}
          disabled={disabled || displayValue >= max}
          className="flex items-center justify-center w-10 h-10 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation active:scale-95 transition-all"
        >
          <Plus className="w-5 h-5" />
        </button>
      </div>

      {/* Score Status Indicator */}
      {value !== null && (
        <div className="text-center">
          {isUnderPar && (
            <span className="text-sm font-medium text-green-600">
              -{par - value} under par
            </span>
          )}
          {isOverPar && (
            <span className="text-sm font-medium text-red-600">
              +{value - par} over par
            </span>
          )}
          {isParScore && (
            <span className="text-sm font-medium text-blue-600">
              Even par
            </span>
          )}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <p className="text-sm text-red-600 text-center font-medium">
          {error}
        </p>
      )}
    </div>
  );
} 